import React from 'react';
import { getCountryByCode, countries } from '@/data/countries';
import { resolveCountryName } from '@/utils/countryResolver';

interface CountryFlagProps {
  /** ISO2, ISO3, or full country name */
  code?: string | null;
  showName?: boolean;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const SIZE_MAP = {
  xs: { width: 32, imgClass: 'w-4 h-3', fallbackClass: 'w-4 h-3 text-[10px]' },
  sm: { width: 40, imgClass: 'w-5 h-[15px]', fallbackClass: 'w-5 h-[15px] text-xs' },
  md: { width: 48, imgClass: 'w-6 h-[18px]', fallbackClass: 'w-6 h-[18px] text-sm' },
  lg: { width: 64, imgClass: 'w-8 h-6', fallbackClass: 'w-8 h-6 text-base' },
  xl: { width: 80, imgClass: 'w-10 h-[30px]', fallbackClass: 'w-10 h-[30px] text-lg' },
};

/** Returns a flagcdn.com URL for a given ISO2 country code. Works on ALL platforms. */
export function getFlagUrl(iso2: string, width = 40): string {
  return `https://flagcdn.com/w${width}/${iso2.toLowerCase()}.png`;
}

/** Returns a flagcdn.com srcSet for retina displays. */
export function getFlagSrcSet(iso2: string, width = 40): string {
  const code = iso2.toLowerCase();
  return `https://flagcdn.com/w${width}/${code}.png 1x, https://flagcdn.com/w${width * 2}/${code}.png 2x`;
}

function getFlagEmoji(iso2: string): string {
  const normalized = iso2.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return normalized;
  return String.fromCodePoint(...normalized.split('').map(char => 127397 + char.charCodeAt(0)));
}

/**
 * Inline flag image helper — replaces all emoji flag rendering.
 * Works on Windows, Linux, macOS — all browsers.
 */
export const FlagImg = React.forwardRef<HTMLElement, {
  code: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}>(function FlagImg({
  code,
  size = 'sm',
  className,
}, ref) {
  const [hasError, setHasError] = React.useState(false);
  if (!code) return null;

  const { imgClass, width, fallbackClass } = SIZE_MAP[size];

  if (hasError) {
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        aria-hidden="true"
        className={`${fallbackClass} inline-flex items-center justify-center rounded-[2px] bg-secondary/60 text-foreground shrink-0 overflow-hidden ${className ?? ''}`}
      >
        {getFlagEmoji(code)}
      </span>
    );
  }

  return (
    <img
      ref={ref as React.Ref<HTMLImageElement>}
      src={getFlagUrl(code, width)}
      srcSet={getFlagSrcSet(code, width)}
      alt={code.toUpperCase()}
      aria-hidden="true"
      draggable={false}
      className={`${imgClass} inline-block rounded-[2px] object-cover shrink-0 ${className ?? ''}`}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
});

FlagImg.displayName = 'FlagImg';

/**
 * Renders a country flag as an IMAGE (not emoji). Works on Windows, Linux, macOS.
 * Accepts ISO2, ISO3, or full country name strings.
 */
export function CountryFlag({ code, showName = true, className, size = 'sm' }: CountryFlagProps) {
  if (!code) return <span className="text-muted-foreground">—</span>;

  // Try direct ISO2 lookup first
  const direct = getCountryByCode(code.trim().toUpperCase());
  if (direct) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className ?? ''}`}>
        <FlagImg code={direct.code} size={size} />
        {showName && <span>{direct.name}</span>}
      </span>
    );
  }

  // Resolve ISO3 or full name → canonical name, then look up
  const resolvedName = resolveCountryName(code);
  const byName = countries.find(c => c.name === resolvedName);
  if (byName) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className ?? ''}`}>
        <FlagImg code={byName.code} size={size} />
        {showName && <span>{byName.name}</span>}
      </span>
    );
  }

  // Last resort: plain text
  return <span className={className}>{resolvedName || code}</span>;
}
